import type { MindMapDocument } from '@/model/document';
import type { MapSnapshot } from '@/model/types';
import type { ImportedNode, ImportedTree } from './types';

/**
 * Apply an ImportedTree to a MindMapDocument.
 *
 * Initializes the document with the root node text, then recursively
 * creates child nodes via mapDoc.addChild().
 */
export function applyImport(mapDoc: MindMapDocument, tree: ImportedTree): void {
  const rootId = mapDoc.initialize(tree.root.text);

  // Set root text (initialize uses 'Central Idea' then we rename)
  mapDoc.updateNodeText(rootId, tree.root.text);

  if (tree.root.notes) {
    mapDoc.updateNodeNotes(rootId, tree.root.notes);
  }

  // Recursively add children
  for (const child of tree.root.children) {
    addChildRecursive(mapDoc, rootId, child);
  }
}

function addChildRecursive(
  mapDoc: MindMapDocument,
  parentId: string,
  node: ImportedNode,
): void {
  const childId = mapDoc.addChild(parentId, node.text);

  if (node.notes) {
    mapDoc.updateNodeNotes(childId, node.notes);
  }

  for (const child of node.children) {
    addChildRecursive(mapDoc, childId, child);
  }
}

/**
 * Apply a MapSnapshot to a MindMapDocument.
 *
 * Initializes the document structure from the snapshot by walking the
 * node tree starting from the root. Connections are not imported in this
 * phase since they require all node IDs to be present first.
 */
export function applySnapshot(mapDoc: MindMapDocument, snapshot: MapSnapshot): void {
  const rootNodeId = snapshot.meta.rootNodeId;
  const rootNodeData = snapshot.nodes[rootNodeId];

  if (!rootNodeData) {
    throw new Error('Snapshot root node not found');
  }

  const rootId = mapDoc.initialize(snapshot.meta.title);
  mapDoc.updateNodeText(rootId, rootNodeData.text);

  if (rootNodeData.notes) {
    mapDoc.updateNodeNotes(rootId, rootNodeData.notes);
  }

  // Build node ID mapping: original ID -> new ID
  const idMap = new Map<string, string>();
  idMap.set(rootNodeId, rootId);

  // Recursively add children
  addSnapshotChildren(mapDoc, snapshot, rootNodeId, rootId, idMap);

  // Add connections using the ID mapping
  for (const conn of Object.values(snapshot.connections)) {
    const sourceId = idMap.get(conn.sourceNodeId);
    const targetId = idMap.get(conn.targetNodeId);
    if (sourceId && targetId) {
      mapDoc.addConnection(sourceId, targetId, conn.label);
    }
  }
}

function addSnapshotChildren(
  mapDoc: MindMapDocument,
  snapshot: MapSnapshot,
  originalParentId: string,
  newParentId: string,
  idMap: Map<string, string>,
): void {
  const parentData = snapshot.nodes[originalParentId];
  if (!parentData) return;

  for (const originalChildId of parentData.childIds) {
    const childData = snapshot.nodes[originalChildId];
    if (!childData) continue;

    const newChildId = mapDoc.addChild(newParentId, childData.text);
    idMap.set(originalChildId, newChildId);

    if (childData.notes) {
      mapDoc.updateNodeNotes(newChildId, childData.notes);
    }

    addSnapshotChildren(mapDoc, snapshot, originalChildId, newChildId, idMap);
  }
}
